import type { ComponentProps, FC, ReactNode } from 'react'
import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-transparent font-medium whitespace-nowrap transition-[background-color,border-color,color,transform] duration-200 ease-out select-none active:translate-y-px disabled:pointer-events-none disabled:opacity-55 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-primary-hover',
                ink: 'bg-ink text-ink-foreground hover:bg-ink/85',
                outline: 'border-foreground/25 bg-transparent text-foreground hover:border-foreground hover:bg-foreground/[0.04]',
                inverse: 'border-ink-foreground/30 bg-transparent text-ink-foreground hover:border-ink-foreground hover:bg-ink-foreground/10',
                ghost: 'text-foreground hover:bg-foreground/5'
            },
            size: {
                sm: 'h-9 px-3.5 text-sm',
                default: 'h-11 px-5 text-[0.95rem]',
                lg: 'h-13 px-6 text-base',
                icon: 'size-11'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
)

type ButtonProps = ComponentProps<'button'> & VariantProps<typeof buttonVariants>

const Button: FC<ButtonProps> = ({
    className,
    variant,
    size,
    type = 'button',
    ...props
}): ReactNode => {
    return (
        <button
            type={type}
            data-slot='button'
            className={cn(buttonVariants({ variant, size }), className)}
            {...props}
        />
    )
}

export { Button, buttonVariants }
